import MuleBadge from './MuleBadge';
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Users, Loader, AlertCircle, Pin } from 'lucide-react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { DriverCard } from './DriverCard';
import { Order } from '../services/orders.service';
import MapViewIcon from './icons/MapViewIcon';
import { createTruckMarkerIcon } from '../utils/truckMarkerUtils';
import MultiSelect, { MultiSelectOption } from './MultiSelect';
import { SuggestedDriver } from '../hooks/useDriverDrawer';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Driver information with location, distance, and qualification data
 */
export interface EnrichedDriver {
  isMule?: boolean;
  unitNumber: string;
  driverName: string;
  vehicleName?: string;
  truckVin?: string;
  isQualified: boolean;
  dispatcher?: string;
  driveTimeRemaining?: string | null;
  location: {
    latitude: number;
    longitude: number;
    speed?: number;
    hosStatus?: string | null;
    time?: string;
  } | null;
  distanceFromOrigin: number | null;
  dispatchInfo: {
    proNumber: string;
    isCompleted: boolean;
    originName: string;
    destinationName: string;
  } | null;
  attributes?: string[];
  [key: string]: any;
}

/**
 * Props for the DriverDrawer component
 */
export interface DriverDrawerProps {
  /** Whether the drawer is open */
  open: boolean;

  /** Callback to close the drawer */
  onClose: () => void;

  /** Whether the drawer is pinned (optional) */
  isPinned?: boolean;

  /** Callback when pin state changes (optional) */
  onPinChange?: (pinned: boolean) => void;

  /** The order to find drivers for */
  order: Order | null;

  /** List of available drivers with enriched data */
  drivers: EnrichedDriver[];

  /** Top suggested drivers from smart dispatch scoring */
  suggestedDrivers?: SuggestedDriver[];

  /** Whether drivers are currently being loaded */
  loading: boolean;

  /** Error message if driver fetch failed */
  error: string | null;

  /** Callback when a driver is selected */
  onSelectDriver: (driver: EnrichedDriver) => void;

  /** Whether Google Maps is loaded (passed from parent) */
  isMapLoaded?: boolean;

  /** Whether to show map view by default when drawer opens */
  defaultShowMap?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * DriverDrawer - Right-side drawer for selecting a driver for an order
 *
 * Features:
 * - Displays order summary (customer, origin, destination, requirements)
 * - Search/filter drivers by name or unit number
 * - Separates qualified vs unqualified drivers
 * - Shows driver details: distance, availability, current dispatch
 * - Loading and error states
 *
 * Usage:
 * ```tsx
 * <DriverDrawer
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   order={selectedOrder}
 *   drivers={enrichedDrivers}
 *   loading={isLoading}
 *   error={errorMessage}
 *   onSelectDriver={(driver) => assignDriver(driver)}
 * />
 * ```
 */
export const DriverDrawer: React.FC<DriverDrawerProps> = ({
  open,
  onClose,
  isPinned = false,
  onPinChange,
  order,
  drivers,
  suggestedDrivers = [],
  loading,
  error,
  onSelectDriver,
  isMapLoaded = false,
  defaultShowMap = false,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  // Filter text for searching drivers
  const [filterText, setFilterText] = useState('');

  // Dispatcher filter
  const [selectedDispatchers, setSelectedDispatchers] = useState<string[]>([]);

  // Map view state
  const [showMap, setShowMap] = useState(defaultShowMap);
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>('roadmap');
  const [mapReady, setMapReady] = useState(false);
  const [selectedDriverForMap, setSelectedDriverForMap] = useState<EnrichedDriver | null>(null);
  const [selectedOutOfRangeDriver, setSelectedOutOfRangeDriver] = useState<EnrichedDriver | null>(null);
  const [routePolyline, setRoutePolyline] = useState<google.maps.LatLng[] | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  // Route rendering refs
  const routeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destinationMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Parse coordinate string to LatLngLiteral
   * Handles multiple formats:
   * - "lat,lng" or "lat, lng"
   * - "lat,lng; lat,lng; ..." (takes first pair)
   * - "lat,lng\r\nlat,lng\r\n..." (takes first pair)
   * - Detects and handles reversed lng,lat order
   */
  const parseCoordinates = useCallback((coordString: string | null): google.maps.LatLngLiteral | null => {
    if (!coordString) return null;

    // Remove any carriage returns and normalize
    let cleaned = coordString.replace(/\r/g, '').trim();

    // Handle multiple coordinate pairs (semicolon or newline separated) - take first pair
    if (cleaned.includes(';')) {
      cleaned = cleaned.split(';')[0].trim();
    } else if (cleaned.includes('\n')) {
      cleaned = cleaned.split('\n')[0].trim();
    }

    // Split by comma
    const parts = cleaned.split(',').map(s => parseFloat(s.trim()));

    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      // Detect if coordinates are reversed (lng,lat instead of lat,lng)
      // Latitude range: -90 to 90, Longitude range: -180 to 180
      // If first value is > 90 or < -90, it's likely longitude
      if (Math.abs(parts[0]) > 90) {
        // Coordinates are reversed (lng,lat), swap them
        return { lat: parts[1], lng: parts[0] };
      }
      // Normal order (lat,lng)
      return { lat: parts[0], lng: parts[1] };
    }

    return null;
  }, []);

  /**
   * Calculate shortest distance from a point to a polyline (route)
   * @param point Driver's location
   * @param polyline Array of LatLng points forming the route
   * @returns Distance in miles
   */
  const calculateDistanceToRoute = useCallback((
    point: { lat: number; lng: number },
    polyline: google.maps.LatLng[] | null
  ): number | null => {
    if (!polyline || polyline.length === 0) return null;

    const R = 3959; // Earth's radius in miles

    // Helper: Haversine distance between two points
    const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const dLat = (lat2 - lat1) * (Math.PI / 180);
      const dLng = (lng2 - lng1) * (Math.PI / 180);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    // Helper: Distance from point to line segment
    const distanceToSegment = (
      px: number, py: number,
      x1: number, y1: number,
      x2: number, y2: number
    ): number => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const lengthSquared = dx * dx + dy * dy;

      if (lengthSquared === 0) {
        // Segment is actually a point
        return haversineDistance(py, px, y1, x1);
      }

      // Calculate projection of point onto line segment (clamped to [0,1])
      let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
      t = Math.max(0, Math.min(1, t));

      // Find closest point on segment
      const closestX = x1 + t * dx;
      const closestY = y1 + t * dy;

      return haversineDistance(py, px, closestY, closestX);
    };

    // Find minimum distance to any segment of the route
    let minDistance = Infinity;

    for (let i = 0; i < polyline.length - 1; i++) {
      const segmentDistance = distanceToSegment(
        point.lng, point.lat,
        polyline[i].lng(), polyline[i].lat(),
        polyline[i + 1].lng(), polyline[i + 1].lat()
      );
      minDistance = Math.min(minDistance, segmentDistance);
    }

    return minDistance === Infinity ? null : minDistance;
  }, []);

  /**
   * Clear route and destination markers
   */
  const clearRoute = useCallback(() => {
    if (routeRendererRef.current) {
      routeRendererRef.current.setMap(null);
      routeRendererRef.current = null;
    }
    if (originMarkerRef.current) {
      originMarkerRef.current.map = null;
      originMarkerRef.current = null;
    }
    if (destinationMarkerRef.current) {
      destinationMarkerRef.current.map = null;
      destinationMarkerRef.current = null;
    }
    setRoutePolyline(null);
  }, []);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get unique dispatchers from drivers
   */
  const dispatcherOptions = useMemo(() => {
    const dispatchers = new Set<string>();
    drivers.forEach(driver => {
      if (driver.dispatcher) {
        dispatchers.add(driver.dispatcher);
      }
    });
    return Array.from(dispatchers)
      .sort()
      .map(dispatcher => ({
        value: dispatcher,
        label: dispatcher,
      }));
  }, [drivers]);

  /**
   * Filter drivers based on search text and dispatcher selection
   * Searches: unitNumber and driverName (case-insensitive)
   */
  const filteredDrivers = useMemo(() => {
    let filtered = drivers;

    // Filter by search text
    if (filterText) {
      const searchLower = filterText.toLowerCase();
      filtered = filtered.filter((driver) => {
        return (
          driver.unitNumber?.toLowerCase().includes(searchLower) ||
          driver.driverName?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by selected dispatchers
    if (selectedDispatchers.length > 0) {
      filtered = filtered.filter((driver) => {
        return driver.dispatcher && selectedDispatchers.includes(driver.dispatcher);
      });
    }

    return filtered;
  }, [drivers, filterText, selectedDispatchers]);

  /**
   * Extract order requirements for display
   */
  const orderRequirements = useMemo(() => {
    if (!order) return [];

    const reqs = [];
    if (order.hazmat === 'Y') reqs.push('Hazmat');
    if (order.isDaycab === 'Y') reqs.push('Daycab');
    if (order.isTank === 'Y') reqs.push('Tank');
    if (order.overweightPermitRequired === 'Y') reqs.push('93K');
    return reqs;
  }, [order]);

  /**
   * Count qualified drivers
   */
  const qualifiedCount = useMemo(() => {
    return filteredDrivers.filter(d => d.isQualified).length;
  }, [filteredDrivers]);

  /**
   * Set of suggested driver unit numbers for quick lookup
   */
  const suggestedUnitNumbers = useMemo(() => {
    return new Set(suggestedDrivers.map(s => s.driver));
  }, [suggestedDrivers]);

  /**
   * Suggested drivers matched to enriched driver data, ordered by rank
   */
  const enrichedSuggestedDrivers = useMemo(() => {
    if (suggestedDrivers.length === 0) return [];

    return suggestedDrivers
      .map(suggestion => {
        const enriched = filteredDrivers.find(d => d.unitNumber === suggestion.driver);
        if (!enriched) return null;
        return { ...enriched, suggestionScore: suggestion.total_score, suggestionRank: suggestion.rank };
      })
      .filter(Boolean) as (EnrichedDriver & { suggestionScore: number; suggestionRank: number })[];
  }, [suggestedDrivers, filteredDrivers]);

  /**
   * Qualified drivers excluding those already shown in suggested section
   */
  const remainingQualifiedDrivers = useMemo(() => {
    return filteredDrivers.filter(d => d.isQualified && !suggestedUnitNumbers.has(d.unitNumber));
  }, [filteredDrivers, suggestedUnitNumbers]);

  /**
   * Move selected driver to top of remaining qualified list when map-selected
   */
  const sortedRemainingDrivers = useMemo(() => {
    if (!selectedDriverForMap) return remainingQualifiedDrivers;

    const selected = remainingQualifiedDrivers.find(d => d.unitNumber === selectedDriverForMap.unitNumber);
    if (!selected) return remainingQualifiedDrivers;

    return [selected, ...remainingQualifiedDrivers.filter(d => d.unitNumber !== selectedDriverForMap.unitNumber)];
  }, [remainingQualifiedDrivers, selectedDriverForMap]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Reset all state when drawer is closed
   */
  useEffect(() => {
    if (!open) {
      // Clear all internal state when drawer closes
      setFilterText('');
      setSelectedDispatchers([]);
      setShowMap(false);
      setMapReady(false);
      setSelectedDriverForMap(null);
      setSelectedOutOfRangeDriver(null);
      setRoutePolyline(null);
    }
  }, [open]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle driver selection
   * Calls parent callback and optionally closes drawer
   */
  const handleDriverClick = useCallback((driver: EnrichedDriver) => {
    if (showMap) {
      // In map mode, clicking a driver zooms to their location and moves them to top of list
      setSelectedDriverForMap(driver);

      // Check if driver is out of range (>30 miles)
      const isOutOfRange = driver.distanceFromOrigin !== null && driver.distanceFromOrigin > 30;

      if (isOutOfRange) {
        // Store out-of-range driver to display their marker (don't hide others)
        setSelectedOutOfRangeDriver(driver);
      }
      // Note: Don't clear selectedOutOfRangeDriver when clicking nearby drivers
      // This keeps both nearby drivers AND clicked out-of-range drivers visible

      if (driver.location && mapRef.current) {
        mapRef.current.panTo({
          lat: driver.location.latitude,
          lng: driver.location.longitude,
        });
        mapRef.current.setZoom(12);
      }
    } else {
      // In normal mode, select the driver
      onSelectDriver(driver);
      // Optionally close drawer after selection
      // onClose();
    }
  }, [showMap, onSelectDriver]);

  /**
   * Toggle map view
   */
  const handleToggleMap = useCallback(() => {
    setShowMap(prev => {
      // If closing the map, reset mapReady
      if (prev) {
        setMapReady(false);
        setRoutePolyline(null);
      }
      return !prev;
    });
    setSelectedDriverForMap(null);
    setSelectedOutOfRangeDriver(null);
  }, []);

  /**
   * Handle driver assignment from map view
   */
  const handleAssignDriver = useCallback(() => {
    if (selectedDriverForMap) {
      onSelectDriver(selectedDriverForMap);
      // Reset map state
      setShowMap(false);
      setSelectedDriverForMap(null);
      setSelectedOutOfRangeDriver(null);
    }
  }, [selectedDriverForMap, onSelectDriver]);

  /**
   * Create map markers for drivers with locations
   * Shows all qualified drivers with location data
   */
  const driverMarkers = useMemo(() => {
    return filteredDrivers
      .filter(d => {
        // Only require location data and qualified status
        return d.location && d.isQualified;
      })
      .map(driver => ({
        driver,
        position: {
          lat: driver.location!.latitude,
          lng: driver.location!.longitude,
        },
      }));
  }, [filteredDrivers]);

  /**
   * Calculate map center and bounds
   */
  const mapCenter = useMemo(() => {
    if (driverMarkers.length === 0 && order?.originCoordinates) {
      // Center on origin if no drivers
      const coords = order.originCoordinates.split(',').map(c => parseFloat(c.trim()));
      if (coords.length >= 2) {
        return { lat: coords[0], lng: coords[1] };
      }
    }
    if (driverMarkers.length > 0) {
      // Center on first driver
      return driverMarkers[0].position;
    }
    // Default center (US)
    return { lat: 39.8283, lng: -98.5795 };
  }, [driverMarkers, order]);

  /**
   * Handle map load
   */
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Trigger marker creation by setting mapReady to true
    // This ensures markers are created immediately when map loads
    setMapReady(true);

    // Create bounds to fit all drivers
    if (driverMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds();

      // Add origin if available
      if (order?.originCoordinates) {
        const coords = order.originCoordinates.split(',').map(c => parseFloat(c.trim()));
        if (coords.length >= 2) {
          bounds.extend({ lat: coords[0], lng: coords[1] });
        }
      }

      // Add all driver locations
      driverMarkers.forEach(marker => {
        bounds.extend(marker.position);
      });

      map.fitBounds(bounds);
    }
  }, [driverMarkers, order]);

  /**
   * Update map type when toggled
   */
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapType);
  }, [mapType]);

  /**
   * Create custom markers for drivers with truck icons using AdvancedMarkerElement
   */
  useEffect(() => {
    // Early return if conditions not met
    if (!showMap || !mapReady || !mapRef.current) {
      return;
    }

    // Clear existing markers first
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    // Note: Origin and destination markers are now handled in the route drawing effect

    // Add driver markers with truck icons using AdvancedMarkerElement
    if (window.google?.maps?.marker?.AdvancedMarkerElement) {
      driverMarkers.forEach(({ driver, position }) => {
        const isSelected = selectedDriverForMap?.unitNumber === driver.unitNumber;

        // Determine if driver has an active dispatch
        const hasDispatch = driver.dispatchInfo !== null;
        const isCompleted = driver.dispatchInfo?.isCompleted || false;

        // Create truck icon
        const truckIcon = createTruckMarkerIcon({
          speed: driver.location?.speed || 0,
          heading: undefined, // No heading data available, will default to right-facing
          hasDispatch,
          isCompleted,
          remainingDispatches: 0, // Not available in driver data
          hasOriginArrivalTime: false, // Not available in driver data
          truckType: 'TruckWithContainer',
          sizeMultiplier: 1.2, // Increased by 50% (was 0.8, now 1.2)
          unitNumber: driver.unitNumber,
          isSelected,
          isDarkMode: false, // Could be pulled from theme if needed
          opacity: 1,
          isOOS: false, // Not available in driver data
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          map: mapRef.current!,
          content: truckIcon,
          title: `${driver.unitNumber} - ${driver.driverName}`,
        });

        // Add click listener
        marker.addListener('click', () => {
          handleDriverClick(driver);
        });

        markersRef.current.push(marker);
      });
    }

    // Cleanup function
    return () => {
      markersRef.current.forEach(marker => {
        marker.map = null;
      });
      markersRef.current = [];
    };
  }, [showMap, mapReady, driverMarkers, selectedDriverForMap, selectedOutOfRangeDriver, order]);

  /**
   * Draw route from origin to destination on the map
   */
  useEffect(() => {
    // Early return if conditions not met
    if (!showMap || !mapReady || !mapRef.current || !order) {
      clearRoute();
      return;
    }

    // Parse origin and destination coordinates
    const originCoords = parseCoordinates(order.originCoordinates || null);
    const destCoords = parseCoordinates(order.destinationCoordinates || null);

    // If we don't have both coordinates, clear any existing route
    if (!originCoords || !destCoords) {
      clearRoute();
      return;
    }

    // Clear old route before drawing new one
    clearRoute();

    // Create origin marker (purple pin)
    if (window.google?.maps?.marker?.PinElement) {
      const originPin = new google.maps.marker.PinElement({
        background: '#9333EA', // Purple for origin
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.0,
      });

      originMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: originCoords,
        map: mapRef.current,
        content: originPin.element,
        title: 'Origin: ' + order.originName,
        zIndex: 10000, // Higher z-index to be above route
      });

      // Create destination marker (red pin)
      const destPin = new google.maps.marker.PinElement({
        background: '#EF4444', // Red for destination
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.0,
      });

      destinationMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: destCoords,
        map: mapRef.current,
        content: destPin.element,
        title: 'Destination: ' + order.destinationName,
        zIndex: 10000, // Higher z-index to be above route
      });
    }

    // Draw route using DirectionsService
    const directionsService = new google.maps.DirectionsService();

    directionsService.route(
      {
        origin: originCoords,
        destination: destCoords,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          // Extract and store the route polyline for distance calculations
          if (result.routes[0]?.overview_path) {
            const polyline = result.routes[0].overview_path;
            setRoutePolyline(polyline);
          }

          // Create directions renderer with purple route
          if (!routeRendererRef.current) {
            routeRendererRef.current = new google.maps.DirectionsRenderer({
              map: mapRef.current,
              suppressMarkers: true, // We're using custom markers
              polylineOptions: {
                strokeColor: '#9333EA', // Purple route
                strokeWeight: 4,
                strokeOpacity: 0.7,
              },
            });
          }
          routeRendererRef.current.setDirections(result);

          // Fit map to show entire route
          if (result.routes[0] && result.routes[0].bounds) {
            mapRef.current?.fitBounds(result.routes[0].bounds);
          }
        }
      }
    );

    // Cleanup function
    return () => {
      clearRoute();
    };
  }, [showMap, mapReady, order, clearRoute, parseCoordinates]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Don't render anything if drawer is closed
  if (!open) return null;

  // Custom drawer implementation with portal to render on top of OrderDetailsDrawer
  // Uses z-[9999] to ensure it appears above the dialog's top-layer backdrop
  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: 9999 }}
      onClick={(e) => {
        // Close if clicking on backdrop (outside the drawer panel)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Drawer Panel - slides in from right */}
      <div
        className={`absolute top-0 right-0 h-full bg-white dark:bg-gray-800 shadow-2xl flex flex-row transform transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: showMap ? '50vw' : '25vw', minWidth: showMap ? '700px' : '350px', zIndex: 9999 }}
      >
        {/* ========================================================================
            LEFT SIDE: DRIVER LIST
            ======================================================================== */}
        <div className="flex flex-col" style={{ width: showMap ? '350px' : '100%' }}>
          {/* DRAWER HEADER */}
          <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700 bg-theme-bg-tertiary">
            <h2 className="text-base font-semibold text-heading">
              {order
                ? `Suggest Driver - PRO #${order.pronumber}${order.legnum ? ` Leg ${order.legnum}` : ''}`
                : 'Suggest Driver'}
            </h2>
            <div className="flex items-center gap-2">
              {/* Pin Button */}
              {onPinChange && (
                <button
                  onClick={() => onPinChange(!isPinned)}
                  className={`p-1.5 rounded-md transition-colors ${
                    isPinned
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-muted'
                  }`}
                  aria-label={isPinned ? 'Unpin drawer (show popup by default)' : 'Pin drawer (open drawer by default)'}
                  title={isPinned ? 'Unpin drawer (show popup by default)' : 'Pin drawer (open drawer by default)'}
                >
                  <Pin className={`h-5 w-5 ${isPinned ? 'fill-current' : ''}`} />
                </button>
              )}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close drawer"
              >
                <X className="h-5 w-5 text-muted" />
              </button>
            </div>
          </div>

        {/* DRAWER CONTENT */}
        <div className="flex-1 overflow-y-auto">
        {/* Order Summary Section */}
        {order && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-700">
            <div className="space-y-2">
              {/* Customer */}
              <div>
                <p className="text-xs text-muted">Customer</p>
                <p className="text-sm font-medium text-heading truncate">
                  {order.customerName || 'N/A'}
                </p>
              </div>

              {/* Origin & Destination */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted">Origin</p>
                  <p className="text-sm text-label truncate">
                    {order.originName || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted">Destination</p>
                  <p className="text-sm text-label truncate">
                    {order.destinationName || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Requirements */}
              {orderRequirements.length > 0 && (
                <div>
                  <p className="text-xs text-muted mb-1">Requirements</p>
                  <div className="flex flex-wrap gap-1">
                    {orderRequirements.map(req => (
                      <span
                        key={req}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info text-info-fg"
                      >
                        {req}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search/Filter Section */}
        <div className="px-4 py-3 border-b dark:border-gray-700">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Filter by name or unit number..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Dispatcher Filter */}
          {dispatcherOptions.length > 0 && (
            <div className="mt-3">
              <MultiSelect
                options={dispatcherOptions}
                value={selectedDispatchers}
                onChange={setSelectedDispatchers}
                placeholder="Filter by dispatcher..."
                showClearOption={true}
                clearOptionLabel="All Dispatchers"
                enableSearch={true}
                searchPlaceholder="Search dispatchers..."
                countThreshold={2}
              />
            </div>
          )}

          {/* Driver Count */}
          {!loading && (
            <div className="mt-2 flex items-center justify-between text-xs text-muted">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {filteredDrivers.length} drivers available
              </span>
              {qualifiedCount > 0 && (
                <span className="text-green-600 dark:text-green-400">
                  {qualifiedCount} qualified
                </span>
              )}
            </div>
          )}
        </div>

        {/* Driver List Section */}
        <div className="px-4 py-3">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading available drivers...</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900 dark:text-red-100">Error loading drivers</p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredDrivers.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {filterText ? 'No drivers match your search' : 'No drivers available'}
              </p>
            </div>
          )}

          {/* Map Toggle */}
          {!loading && !error && filteredDrivers.filter(d => d.isQualified).length > 0 && (
            <div className="flex items-center justify-end mb-3">
              <button
                onClick={handleToggleMap}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  showMap
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                    : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <MapViewIcon className="h-4 w-4" />
                <span>{showMap ? 'Hide Map' : 'View On Map'}</span>
              </button>
            </div>
          )}

          {/* Suggested Drivers Section */}
          {!loading && !error && enrichedSuggestedDrivers.length > 0 && (
            <div className="space-y-3 mb-4">
              <h3 className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                Suggested Drivers
              </h3>
              {enrichedSuggestedDrivers.map((driver, index) => (
                <DriverCard
                  key={`suggested-${driver.unitNumber}-${index}`}
                  driver={driver}
                  onClick={() => handleDriverClick(driver)}
                  index={index}
                  isSelected={selectedDriverForMap?.unitNumber === driver.unitNumber}
                  suggestionScore={driver.suggestionScore}
                />
              ))}
            </div>
          )}

          {/* Qualified Drivers Section (excluding suggested) */}
          {!loading && !error && sortedRemainingDrivers.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">
                {enrichedSuggestedDrivers.length > 0 ? 'Other Qualified Drivers' : 'Qualified Drivers'}
              </h3>
              {sortedRemainingDrivers.map((driver, index) => (
                <DriverCard
                  key={`qualified-${driver.unitNumber}-${index}`}
                  driver={driver}
                  onClick={() => handleDriverClick(driver)}
                  index={index}
                  isSelected={selectedDriverForMap?.unitNumber === driver.unitNumber}
                />
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* ========================================================================
          RIGHT SIDE: MAP VIEW
          ======================================================================== */}
      {showMap && (
        <div className="flex-1 flex flex-col border-l border-gray-200 dark:border-gray-700">
          {/* Map Container */}
          <div className="flex-1 relative mb-2">
            {isMapLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={10}
                onLoad={onMapLoad}
                options={{
                  mapId: '3cf64d0fcebdaf11f3625d6e', // Required for AdvancedMarkerElement
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControl: true,
                  clickableIcons: false,
                  gestureHandling: 'greedy', // Allow dragging without modifier keys
                  draggable: true,
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-900">
                <Loader className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
            )}
            {/* Satellite Toggle Button */}
            {isMapLoaded && (
              <button
                onClick={() => setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap')}
                className="absolute top-3 left-3 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 rounded shadow-md p-2 z-10"
                title={mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view'}
              >
                {mapType === 'roadmap' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-600 dark:text-gray-300">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-600 dark:text-gray-300">
                    <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
                    <path d="M9 3v15" />
                    <path d="M15 6v15" />
                  </svg>
                )}
              </button>
            )}
          </div>

          {/* Assign Button Section */}
          {selectedDriverForMap && (
            <div className="p-4 pb-20 border-t border-gray-200 dark:border-gray-700 bg-panel">
              <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm font-medium text-heading">
                  {selectedDriverForMap.unitNumber}<MuleBadge isMule={selectedDriverForMap.isMule} /> - {selectedDriverForMap.driverName}
                </p>
                {selectedDriverForMap.distanceFromOrigin !== null && (
                  <p className="text-xs text-muted mt-1">
                    {selectedDriverForMap.distanceFromOrigin.toFixed(1)} miles from origin
                  </p>
                )}
              </div>
              <button
                onClick={handleAssignDriver}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Users className="h-4 w-4" />
                Assign Driver
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </div>,
    document.body
  );
};

export default DriverDrawer;
